import { Alert } from '@creditornot/cb-alert';
import { Avatar } from '@creditornot/cb-avatar';
import { TextButton, Button } from '@creditornot/cb-button';
import { InputCheckbox } from '@creditornot/cb-checkbox';
import { MagnifyingGlass } from '@creditornot/cb-icons';
import { colors, cssThemedValue, Typography as T } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { ModalHeader } from '@creditornot/cb-modal';
import { Empty } from '@creditornot/cb-placeholders';
import { Tag } from '@creditornot/cb-tag';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { usePluralization } from 'common/hooks';
import { localize } from 'common/utils';
import { VenueWithSharedMenuLinkInfo } from 'modules/shared-menu/hooks/useVenuesWithSelectionLinks';
import { SelectedVenuesV2 } from 'modules/shared-menu/pages/SharedMenuRootPageV2/components/SelectedVenuesV2';
import { SharedMenu } from 'modules/shared-menu/types';

import {
  NoteContainer,
  VenueList,
  ImageContainer,
  NameAndAddress,
  StyledModalFooter,
  VenueItemLeftContent,
  VenueItemRightContent,
  VenueItemRoot,
} from './styles';

type Props = {
  onClose: () => void;
  onContinue: () => void;
  onDeselectAllShownVenues: () => void;
  onVenueRowClick: (venueId: string) => void;
  onSelectAllShownVenues: () => void;
  onSearchVenues: (input: string) => void;
  searchInput: string;
  selectedVenueIds: Set<string>;
  selectedVenues: VenueWithSharedMenuLinkInfo[];
  sharedMenu: SharedMenu;
  shownVenues: VenueWithSharedMenuLinkInfo[];
  selectedShownVenues: VenueWithSharedMenuLinkInfo[];
  selectionDataLoading: boolean;
};

const Root = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const SearchContainer = styled.div`
  padding: 16px 24px;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const SearchInput = styled(Input)`
  width: 100%;
`;

const VenueItem = styled(VenueItemRoot)<{ $selected: boolean }>`
  background-color: ${({ $selected }) => ($selected ? colors.wolt4 : cssThemedValue('surfaceMain'))};
  transition: background-color 100ms;
  cursor: pointer;
  &:hover {
    background-color: ${({ $selected }) =>
      $selected ? colors.wolt8 : cssThemedValue({ light: colors.pepper8, dark: colors.salt4 })};
  }
`;

const FooterActions = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const SelectionContainer = styled.div`
  display: flex;
  width: 100%;
  flex-grow: 1;
  padding-inline-end: 16px;
`;

const VenueCount = styled(T.Body3)`
  color: ${cssThemedValue('textSecondary')};
  margin-inline-end: 16px;
`;

const FooterButtons = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const EmptyMessage = styled(Empty)`
  width: 100%;
  padding: 48px 16px;
`;

export const SelectVenues = ({
  onClose,
  onContinue,
  onDeselectAllShownVenues,
  onSearchVenues,
  onSelectAllShownVenues,
  onVenueRowClick,
  searchInput,
  selectedVenueIds,
  selectedVenues,
  sharedMenu,
  shownVenues,
  selectedShownVenues,
  selectionDataLoading,
}: Props) => {
  const intl = useIntl();
  const { FormattedPluralMessage } = usePluralization();

  return (
    <Root>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage
          id="shared-menu.link.shared-menu-selected.label"
          values={{ name: localize(sharedMenu.name, intl.locale) }}
        />
      </ModalHeader>
      <NoteContainer>
        <Alert variant="info" size="small">
          <FormattedMessage id="shared-menu.link.venues-selected.modal-wolt-specials-note" />
        </Alert>
      </NoteContainer>
      <SearchContainer>
        <SearchInput
          onClearClick={() => onSearchVenues('')}
          value={searchInput}
          icon={<MagnifyingGlass />}
          placeholder={intl.formatMessage({ id: 'shared-menu.toolbar.search-placeholder' })}
          onChange={e => onSearchVenues(e.target.value)}
        />
      </SearchContainer>
      <VenueList>
        {selectedVenues.length > 0 && <SelectedVenuesV2 venues={selectedVenues} onDeselectVenue={onVenueRowClick} />}
        {(!shownVenues || shownVenues.length === 0) && (
          <EmptyMessage title={intl.formatMessage({ id: 'menu.menu-sync.empty-venue-list' })} />
        )}
        {shownVenues &&
          shownVenues.length > 0 &&
          shownVenues.map(venue => (
            <VenueItem
              key={venue.id}
              $selected={selectedVenueIds.has(venue.id)}
              onClick={() => onVenueRowClick(venue.id)}
            >
              <VenueItemLeftContent>
                <InputCheckbox onChange={() => onVenueRowClick(venue.id)} checked={selectedVenueIds.has(venue.id)} />
                <ImageContainer $addInlineStartMargin>
                  <Avatar size="tiny" imgSrc={venue.listimage ?? undefined} />
                </ImageContainer>
                <NameAndAddress>
                  <T.Body3>{localize(venue.name, intl.locale)}</T.Body3>
                  <T.Small>{venue.address}</T.Small>
                </NameAndAddress>
              </VenueItemLeftContent>
              {venue.sharedMenuLink.scope === sharedMenu.scope.type && (
                <VenueItemRightContent>
                  <Tag size="small" variant="lightBlack">
                    {venue.sharedMenuLink.sharedMenuName}
                  </Tag>
                </VenueItemRightContent>
              )}
              {venue.sharedMenuLink.scope !== sharedMenu.scope.type && venue.sharedMenuLink.scope !== 'none' && (
                <VenueItemRightContent>
                  <Tag size="small" variant="lightBlack">
                    <FormattedMessage id="shared-menu.venue-list.other-scope" />
                  </Tag>
                </VenueItemRightContent>
              )}
            </VenueItem>
          ))}
      </VenueList>
      <StyledModalFooter>
        <FooterActions>
          {shownVenues && (
            <SelectionContainer>
              <VenueCount>
                <FormattedPluralMessage id="shared-menu.toolbar.venues-total" count={shownVenues.length} />
              </VenueCount>
              {shownVenues.length > 0 && (
                <TextButton
                  size="small"
                  onClick={onSelectAllShownVenues}
                  variant="blue"
                  style={{ marginInlineEnd: '16px' }}
                >
                  <FormattedPluralMessage
                    id="shared-menu.link.shared-menu-selected.select-venues"
                    count={shownVenues.length}
                  />
                </TextButton>
              )}
              {selectedShownVenues.length > 0 && (
                <TextButton size="small" onClick={onDeselectAllShownVenues} variant="red">
                  <FormattedPluralMessage
                    id="shared-menu.link.shared-menu-selected.deselect-venues"
                    count={selectedShownVenues.length}
                  />
                </TextButton>
              )}
            </SelectionContainer>
          )}
          <FooterButtons>
            <Button variant="lightBlue" size="small" onClick={onClose} style={{ marginInlineEnd: '8px' }}>
              <FormattedMessage id="state.cancel" />
            </Button>
            <Button
              variant="blue"
              size="small"
              disabled={selectedVenueIds.size === 0 || selectionDataLoading}
              loading={selectionDataLoading}
              onClick={() => {
                onContinue();
              }}
            >
              <FormattedMessage id="state.continue" />
            </Button>
          </FooterButtons>
        </FooterActions>
      </StyledModalFooter>
    </Root>
  );
};
